import { useState, type ReactNode } from 'react';
import { Text, UnstyledButton } from '@mantine/core';
import { Link } from 'react-router-dom';
import type { DailyBite, TodaySummary } from '@nelnela/contracts';
import { tileHref } from '../../lib/play-all';
import { IconPlay, IconReplay } from '../watch/icons';
import { Tile } from './Tile';
import { youtubeStill } from './format';
import { receiptLine, receiptTitle, subtitleOpacity, tileRows, tintFor, variantFor } from './plan';

/**
 * The day's tiles (design `2b` mobile, `2a` desktop, `2c` evening).
 *
 * Everything a tile SAYS was composed on the server and frozen into `plan[].display` — which two
 * patterns repeat in this span, how many of the review words came from the scene just watched,
 * the first sentence of the story. This file only decides how those facts are arranged.
 *
 * That division is deliberate. A client that recomputed the copy would need half the composer and
 * would still disagree with the plan as soon as the underlying state moved; frozen copy is also
 * honest copy, because the tile says what was true when the day was dealt.
 */

export interface BiteTilesProps {
  bite: DailyBite;
  summary: TodaySummary;
  evening: boolean;
  desktop: boolean;
  /** swipe away / ✕ — records nothing, frees the slot */
  onDismiss: (planIdx: number) => void;
  /** the "Left early —" strip's answer (design `2h`) */
  onReason: (planIdx: number, reason: 'too_hard' | 'too_easy' | 'no_time' | 'bored') => void;
  /** the one-pattern tile opens a sheet rather than navigating */
  onPattern: (tile: DailyBite['plan'][number]) => void;
  /** the receipt's "back in N days", once the fold has run. Null until then. */
  daysUntilDue: number | null;
}

export function BiteTiles(p: BiteTilesProps) {
  const rows = tileRows(p.bite.plan, { evening: p.evening });
  if (rows.length === 0) return null;
  return (
    <div
      style={
        p.desktop && !p.evening
          ? { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, alignItems: 'start' }
          : { display: 'flex', flexDirection: 'column', gap: p.evening ? 8 : 9 }
      }
    >
      {rows.map((row) =>
        row.kind === 'half' ? (
          <div key={row.tiles.map((t) => t.idx).join('-')} style={{ display: 'flex', gap: 9 }}>
            {row.tiles.map((t) => (
              <div key={t.idx} style={{ flex: 1, minWidth: 0 }}>
                <BiteTile {...p} tile={t} />
              </div>
            ))}
          </div>
        ) : (
          <BiteTile key={row.tiles[0]!.idx} {...p} tile={row.tiles[0]!} />
        ),
      )}
    </div>
  );
}

function BiteTile({ tile, ...p }: BiteTilesProps & { tile: DailyBite['plan'][number] }) {
  const variant = variantFor(tile, { evening: p.evening });
  const href = tileHref(p.bite, tile);
  const display = tile.display;
  const isPattern =
    tile.activityType === 'lesson' || (tile.activityType === 'review' && !!tile.target.lessonId);

  // The hero: the watch tile, with the still and the subtitle pair over it (`2b`). Only in the
  // day state — the evening's completed rows are all the same shape.
  if (tile.activityType === 'watch' && !p.evening && variant !== 'completed') {
    return <HeroTile {...p} tile={tile} href={href} />;
  }

  if (variant === 'receipt') {
    return (
      <Tile
        variant="receipt"
        tint="ember"
        icon={<IconCheck />}
        title={receiptTitle(tile.itemIds.length || 0)}
        meta={receiptLine(p.daysUntilDue)}
      />
    );
  }

  const inner = (
    <Tile
      variant={variant}
      tint={tintFor(tile.activityType)}
      icon={iconFor(tile.activityType)}
      title={display?.title ?? fallbackTitle(tile.activityType)}
      meta={display?.meta}
      why={!p.evening && tile.reason ? <>Why: {tile.reason}</> : undefined}
      {...(p.evening ? {} : { to: isPattern ? undefined : (href ?? undefined) })}
      {...(tile.progressFraction > 0 && !p.evening ? { progress: tile.progressFraction } : {})}
      {...(p.evening && href ? { action: <ReplayLink to={href} /> } : {})}
    >
      {!p.evening && display?.sub && display.sub.length > 0 && (
        <GhostChips chips={display.sub} serif={tile.activityType === 'story'} />
      )}
    </Tile>
  );

  return (
    <Dismissable
      evening={p.evening}
      onDismiss={() => p.onDismiss(tile.idx)}
      onTap={isPattern && !p.evening ? () => p.onPattern(tile) : undefined}
    >
      {inner}
      {tile.abandonedActivityId !== null && !p.evening && (
        <AbandonStrip onPick={(r) => p.onReason(tile.idx, r)} />
      )}
    </Dismissable>
  );
}

/**
 * The watch tile as the hero (`2b`): a full-bleed still with the scene's own line over it, and
 * the English under it at the reveal level.
 *
 * The still is the thumbnail the YouTube embed itself paints, from the player's own host. It is
 * never a per-timestamp frame: that is not achievable for an embed, and M5 settled the question.
 * An upload has no still and gets the plain dark well, which is what the design calls for.
 */
function HeroTile({
  tile,
  bite,
  summary,
  href,
  onDismiss,
  onReason,
}: Pick<BiteTilesProps, 'bite' | 'summary' | 'onDismiss' | 'onReason'> & {
  tile: DailyBite['plan'][number];
  href: string | null;
}) {
  const [stillFailed, setStillFailed] = useState(false);
  const d = tile.display;
  const still =
    d?.still ??
    (summary.continueWatching
      ? youtubeStill(summary.continueWatching.provider, summary.continueWatching.externalId)
      : null);
  const opacity = subtitleOpacity(bite.revealLevel ?? summary.revealLevel);

  const body = (
    <>
      <div
        style={{
          position: 'relative',
          height: 104,
          background: 'var(--mantine-color-dark-9)',
        }}
      >
        {still && !stillFailed && (
          <img
            src={still}
            alt=""
            onError={() => setStillFailed(true)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
        {d?.subtitle && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              padding: '24px 10px 7px',
              background: 'linear-gradient(transparent, rgba(5,7,9,.82))',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <div
              lang="ka"
              style={{ fontFamily: "'Noto Serif Georgian', serif", fontSize: 12, color: '#fff' }}
            >
              {d.subtitle.ka}
            </div>
            {/* The fade meter (`2d`), in one of its exactly two places. The Georgian never fades;
                the English does, and at zero it is simply gone. That is graduation. */}
            {d.subtitle.en && (
              <div lang="en" style={{ fontSize: 10, color: '#cfd6e0', opacity, marginTop: 2 }}>
                {d.subtitle.en}
              </div>
            )}
          </div>
        )}
        {d?.chip && (
          <span
            style={{
              position: 'absolute',
              right: 8,
              top: 8,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '3px 8px',
              borderRadius: 999,
              background: 'rgba(5,7,9,.6)',
              fontSize: 10,
              color: '#e8ebf0',
              pointerEvents: 'none',
            }}
          >
            <span style={{ display: 'flex', transform: 'scale(.4)', width: 9, height: 9 }}>
              <IconPlay />
            </span>
            {d.chip}
          </span>
        )}
      </div>
      <div style={{ padding: '9px 13px 11px' }}>
        <div style={{ fontSize: 14, color: 'var(--nn-text-strong)', lineHeight: 1.4 }}>
          {d?.title ?? 'Watch'}
        </div>
        {d?.meta && (
          <div style={{ marginTop: 3, fontSize: 11.5, color: 'var(--nn-muted)' }}>{d.meta}</div>
        )}
        {tile.reason && (
          <div
            style={{
              marginTop: 7,
              background: 'color-mix(in srgb, var(--nn-text) 5%, transparent)',
              borderRadius: 7,
              padding: '5px 9px',
              fontSize: 10.5,
              lineHeight: 1.45,
              color: 'var(--nn-muted)',
            }}
          >
            Why: {tile.reason}
          </div>
        )}
      </div>
      {tile.progressFraction > 0 && (
        <>
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              height: 3,
              width: '100%',
              background: 'color-mix(in srgb, var(--nn-text) 5%, transparent)',
            }}
          />
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              height: 3,
              width: `${Math.min(100, tile.progressFraction * 100)}%`,
              background: 'var(--nn-accent)',
            }}
          />
        </>
      )}
    </>
  );

  const shell: React.CSSProperties = {
    position: 'relative',
    background: 'var(--nn-panel)',
    border: '1px solid var(--nn-hairline)',
    borderRadius: 10,
    overflow: 'hidden',
    display: 'block',
    textDecoration: 'none',
    color: 'inherit',
  };

  return (
    <Dismissable evening={false} onDismiss={() => onDismiss(tile.idx)}>
      {href ? (
        <Link to={href} style={shell}>
          {body}
        </Link>
      ) : (
        <div style={shell}>{body}</div>
      )}
      {tile.abandonedActivityId !== null && <AbandonStrip onPick={(r) => onReason(tile.idx, r)} />}
    </Dismissable>
  );
}

/**
 * Swipe away — "not today" (design README §6).
 *
 * A horizontal drag past the threshold dismisses; anything shorter springs back. The vertical
 * axis is left entirely alone so the gesture never fights the page's own scroll, which on a
 * phone is the only thing worse than a control that does not work.
 *
 * Dismissing records NOTHING (contract): the tile is gone for the day and the model learns
 * nothing from it. A "not today" is not a judgement about the material.
 */
const SWIPE_THRESHOLD_PX = 90;

function Dismissable({
  children,
  evening,
  onDismiss,
  onTap,
}: {
  children: ReactNode;
  evening: boolean;
  onDismiss: () => void;
  onTap?: () => void;
}) {
  const [dx, setDx] = useState(0);
  const [from, setFrom] = useState<number | null>(null);

  if (evening) return <div style={{ position: 'relative' }}>{children}</div>;

  return (
    <div
      style={{
        position: 'relative',
        transform: dx ? `translateX(${dx}px)` : undefined,
        opacity: dx ? Math.max(0.3, 1 - Math.abs(dx) / (SWIPE_THRESHOLD_PX * 2)) : 1,
        transition: from === null ? 'transform .16s, opacity .16s' : undefined,
        touchAction: 'pan-y',
      }}
      onPointerDown={(e) => setFrom(e.clientX)}
      onPointerMove={(e) => {
        if (from === null) return;
        setDx(e.clientX - from);
      }}
      onPointerUp={() => {
        if (Math.abs(dx) >= SWIPE_THRESHOLD_PX) onDismiss();
        else if (Math.abs(dx) < 4 && onTap) onTap();
        setFrom(null);
        setDx(0);
      }}
      onPointerCancel={() => {
        setFrom(null);
        setDx(0);
      }}
    >
      {children}
    </div>
  );
}

/**
 * "Left early —" (design `2h`).
 *
 * Shown once, for an activity that was abandoned. The chip labelled "boring" sends `'bored'`,
 * which is the enum the schema has; the label is the word a person would use and the value is
 * the word the column accepts.
 */
function AbandonStrip({
  onPick,
}: {
  onPick: (r: 'too_hard' | 'too_easy' | 'no_time' | 'bored') => void;
}) {
  const [gone, setGone] = useState(false);
  if (gone) return null;
  const chips: Array<[string, 'too_hard' | 'too_easy' | 'no_time' | 'bored']> = [
    ['too hard', 'too_hard'],
    ['too easy', 'too_easy'],
    ['no time', 'no_time'],
    ['boring', 'bored'],
  ];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
        marginTop: 6,
        padding: '6px 10px',
        borderRadius: 8,
        background: 'color-mix(in srgb, var(--nn-text) 4%, transparent)',
      }}
    >
      <span style={{ fontSize: 10.5, color: 'var(--nn-faint)' }}>Left early —</span>
      {chips.map(([label, value]) => (
        <UnstyledButton
          key={value}
          onClick={() => {
            onPick(value);
            setGone(true);
          }}
          style={{
            fontSize: 10.5,
            padding: '3px 9px',
            borderRadius: 999,
            border: '1px solid var(--nn-hairline)',
            color: 'var(--nn-muted)',
          }}
        >
          {label}
        </UnstyledButton>
      ))}
      <span style={{ flex: 1 }} />
      <UnstyledButton
        onClick={() => setGone(true)}
        aria-label="Dismiss"
        style={{ fontSize: 11, color: 'var(--nn-faint)', padding: '0 4px' }}
      >
        ✕
      </UnstyledButton>
    </div>
  );
}

/** The review tile's ghost chips, and the read tile's first line (`2b`). */
function GhostChips({ chips, serif }: { chips: string[]; serif: boolean }) {
  if (serif) {
    return (
      <span
        lang="ka"
        style={{
          fontFamily: "'Noto Serif Georgian', serif",
          fontSize: 12.5,
          lineHeight: 1.5,
          color: 'var(--nn-muted)',
        }}
      >
        {chips[0]}
      </span>
    );
  }
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
      {chips.map((c) => (
        <span
          key={c}
          lang="ka"
          style={{
            padding: '3px 9px',
            borderRadius: 999,
            background: 'color-mix(in srgb, var(--nn-text) 4%, transparent)',
            border: '1px solid var(--nn-hairline)',
            fontSize: 10.5,
            color: 'var(--nn-faint)',
          }}
        >
          {c}
        </span>
      ))}
    </div>
  );
}

function ReplayLink({ to }: { to: string }) {
  return (
    <Link to={to} style={{ fontSize: 11, color: 'var(--nn-accent-text)', textDecoration: 'none' }}>
      replay
    </Link>
  );
}

function fallbackTitle(activityType: string): string {
  switch (activityType) {
    case 'review':
      return 'Review';
    case 'story':
      return 'Read a story';
    case 'culture':
      return 'A note';
    case 'listen':
      return 'Listen';
    default:
      return 'Practice';
  }
}

function iconFor(activityType: string): ReactNode {
  switch (activityType) {
    case 'review':
      return <IconReplay />;
    case 'listen':
      return <IconEar />;
    case 'story':
      return <span style={{ fontSize: 13 }}>ა</span>;
    // `3d` is about the world behind the show rather than the language in it, so it gets a mark
    // rather than a letter — the one tile on Today that is not Georgian.
    case 'culture':
      return <span style={{ fontSize: 12 }}>◈</span>;
    case 'lesson':
      return <span style={{ fontSize: 12, fontWeight: 600 }}>ხ</span>;
    default:
      return <IconPlay />;
  }
}

const IconEar = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    aria-hidden
  >
    <path d="M4 14a8 8 0 0 1 16 0" />
    <rect x="3.2" y="14" width="4" height="6" rx="1.6" fill="currentColor" stroke="none" />
    <rect x="16.8" y="14" width="4" height="6" rx="1.6" fill="currentColor" stroke="none" />
  </svg>
);

const IconCheck = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="6,12.5 10,16.5 18,8" />
  </svg>
);

/** The evening's "Give me more" and its closing line (`2c`). Today stops asking. */
export function EveningClose({ onMore, busy }: { onMore: () => void; busy: boolean }) {
  return (
    <>
      <div style={{ marginTop: 12 }}>
        <UnstyledButton
          onClick={onMore}
          disabled={busy}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            padding: '10px 0',
            borderRadius: 999,
            border: '1px solid color-mix(in srgb, var(--nn-text) 12%, transparent)',
            fontSize: 12,
            color: 'var(--nn-text)',
          }}
        >
          {busy ? 'Dealing…' : 'Give me more'}
        </UnstyledButton>
      </div>
      <Text fz={11.5} c="var(--nn-faint)" ta="center" mt={22}>
        It all counted. ხვალამდე — see you tomorrow.
      </Text>
    </>
  );
}
